import React, { useState } from "react";
import { updateUserAttribute, confirmUserAttribute, sendUserAttributeVerificationCode } from "aws-amplify/auth";
import { post } from "aws-amplify/api";
import "./UserProfile.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Cognitoのエラーを日本語にする
const translateEmailError = (e) => {
  switch (e?.name) {
    case 'CodeMismatchException': return '確認コードが正しくありません';
    case 'ExpiredCodeException': return '確認コードの有効期限が切れています。コードを再送してください';
    case 'LimitExceededException':
    case 'TooManyRequestsException': return '試行回数の上限に達しました。しばらく時間をおいてから再度お試しください';
    case 'AliasExistsException': return 'このメールアドレスは既に別のアカウントで使われています';
    case 'InvalidParameterException': return 'メールアドレスの形式が正しくありません';
    default: return 'エラーが発生しました。時間をおいて再度お試しください';
  }
};

// メールアドレス変更：新しいアドレスに届く確認コードで本人確認してから反映する
function EmailEditor({ currentEmail, onChanged }) {
  const [step, setStep] = useState('view'); // view / input / confirm
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const reset = () => {
    setStep('view');
    setNewEmail('');
    setCode('');
    setError('');
  };

  // Cognito上で確認済みになったメールアドレスをDBに反映
  const syncEmail = async () => {
    const op = post({ apiName: 'worktimerApi', path: '/users/me/sync-email' });
    const { body } = await op.response;
    const data = await body.json();
    onChanged(data.email);
    setNotice('メールアドレスを変更しました');
    reset();
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const email = newEmail.trim();
    if (!EMAIL_PATTERN.test(email)) { setError('メールアドレスの形式が正しくありません'); return; }
    if (email.toLowerCase() === (currentEmail || '').toLowerCase()) { setError('現在と同じメールアドレスです'); return; }
    setBusy(true);
    try {
      const result = await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: email } });
      if (result.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        setStep('confirm');
      } else {
        await syncEmail();
      }
    } catch (err) {
      console.error('メールアドレス変更エラー:', err);
      setError(translateEmailError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code.trim())) { setError('6桁の確認コードを入力してください'); return; }
    setBusy(true);
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code.trim() });
      await syncEmail();
    } catch (err) {
      console.error('確認コードエラー:', err);
      setError(err?.name ? translateEmailError(err) : 'メールアドレスの反映に失敗しました。ページを再読み込みしてお試しください');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setBusy(true);
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
      setNotice('確認コードを再送しました');
    } catch (err) {
      setError(translateEmailError(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'view') {
    return (
      <>
        <div className="email-view-row">
          <span className="email-current">{currentEmail || '未設定'}</span>
          <button className="email-change-btn" onClick={() => { setNotice(''); setStep('input'); }}>変更</button>
        </div>
        {notice && <div className="email-notice">{notice}</div>}
      </>
    );
  }

  return (
    <div className="email-editor">
      {step === 'input' ? (
        <form onSubmit={handleRequest}>
          <label className="email-editor-label">新しいメールアドレス</label>
          <input
            type="email"
            className="email-editor-input"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            maxLength={254}
            autoComplete="email"
            autoFocus
            disabled={busy}
          />
          <p className="email-editor-hint">入力したアドレスに確認コードを送ります。パスワードの再設定や認証コードのお知らせは、このアドレスに届くようになります。</p>
          {error && <div className="email-error">{error}</div>}
          <div className="email-editor-actions">
            <button type="submit" className="email-primary-btn" disabled={busy}>{busy ? '送信中…' : '確認コードを送る'}</button>
            <button type="button" className="email-secondary-btn" onClick={reset} disabled={busy}>キャンセル</button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleConfirm}>
          <p className="email-editor-hint email-editor-hint-strong">
            <strong>{newEmail.trim()}</strong> に届いた6桁の確認コードを入力してください。<br />
            ※ 確認が終わるまで、パスワードの再設定メールは届きません。必ず最後まで完了してください。
          </p>
          <input
            type="text"
            inputMode="numeric"
            className="email-editor-input email-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            autoComplete="one-time-code"
            autoFocus
            disabled={busy}
          />
          {error && <div className="email-error">{error}</div>}
          {notice && <div className="email-notice">{notice}</div>}
          <div className="email-editor-actions">
            <button type="submit" className="email-primary-btn" disabled={busy}>{busy ? '確認中…' : '確認して変更する'}</button>
            <button type="button" className="email-secondary-btn" onClick={handleResend} disabled={busy}>コードを再送</button>
            <button type="button" className="email-link-btn" onClick={() => { setStep('input'); setCode(''); setError(''); setNotice(''); }} disabled={busy}>アドレスを入力し直す</button>
          </div>
        </form>
      )}
    </div>
  );
}

function UserProfile({ user, onBack, onEmailChanged }) {
  if (!user) {
    return (
      <div className="user-profile-container">
        <div className="error-message">ユーザー情報が見つかりません</div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-header">
        <button className="back-button" onClick={onBack}>
          ← 戻る
        </button>
        <h2>プロフィール</h2>
      </div>

      <div className="user-profile-card">
        <div className="profile-avatar-section">
          <div className="profile-avatar-large">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.display_name} />
            ) : (
              (user.display_name || user.username || '?').charAt(0)
            )}
          </div>
          <h3 className="profile-name">{user.display_name || user.username}</h3>
          <p className="profile-username">@{user.username}</p>
        </div>

        {/* 縦1列レイアウト */}
        <div className="profile-info-vertical">
          <div className="profile-info-item">
            <div className="info-label">👤 姓</div>
            <div className="info-value">{user.last_name || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">👤 名</div>
            <div className="info-value">{user.first_name || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">🆔 ユーザー名</div>
            <div className="info-value">{user.username || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">🏷️ 役割</div>
            <div className="info-value">
              {user.role === 'admin' ? '管理者' : user.role === 'user' ? '一般ユーザー' : user.role || "未設定"}
            </div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">📧 メールアドレス</div>
            <div className="info-value">
              <EmailEditor currentEmail={user.email} onChanged={(email) => onEmailChanged?.(email)} />
            </div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">🏢 部署</div>
            <div className="info-value">{user.department || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">💼 役職</div>
            <div className="info-value">{user.position || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">📞 電話番号</div>
            <div className="info-value">{user.phone || "未設定"}</div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">📅 入社日</div>
            <div className="info-value">
              {user.join_date ? new Date(user.join_date).toLocaleDateString('ja-JP') : "未設定"}
            </div>
          </div>

          <div className="profile-info-item">
            <div className="info-label">🗓️ 登録日</div>
            <div className="info-value">
              {user.created_at ? new Date(user.created_at).toLocaleDateString('ja-JP') : "未設定"}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default UserProfile;